import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Small countdown primitive used to gate the OTP send/resend buttons.
 *
 * After a call to `start()`, `secondsLeft` counts down once per second until
 * it hits 0. `active` is a convenience for `secondsLeft > 0`.
 *
 * Why this exists: every OTP send UX (web login, associate join, admin
 * login, and the Resend link on the OTP page) needs the same "disable for
 * N seconds after any attempt" behaviour — whether the attempt succeeded,
 * failed, or network-timed-out — so users can't spam the endpoint by
 * mashing the button while an error toast is still visible. Keeping the
 * timer in a hook instead of duplicating setTimeout bookkeeping in four
 * components is just hygiene.
 */
export function useCooldown(defaultSeconds = 30) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    timerRef.current = window.setTimeout(() => setSecondsLeft((n) => (n > 0 ? n - 1 : 0)), 1000);
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, [secondsLeft]);

  const start = useCallback(
    (seconds: number = defaultSeconds) => {
      setSecondsLeft(Math.max(0, Math.floor(seconds)));
    },
    [defaultSeconds],
  );

  return { secondsLeft, active: secondsLeft > 0, start };
}
